import React, { useEffect, useRef, useState } from 'react';

/**
 * 네이버 지도 렌더링 컴포넌트
 * @param {Object} props
 * @param {Object} props.chartData - 지도 데이터
 * @param {string} props.chartData.viz_type - 차트 유형 ('navermap')
 * @param {Object} props.chartData.data - 지도 데이터
 * @param {Object} props.chartData.data.center - 지도 중심점 {lat, lng}
 * @param {Array} props.chartData.data.markers - 마커 배열 [{lat, lng, title, content, icon?}]
 * @param {Array} props.chartData.data.paths - 경로 배열 [{coords: [[lat,lng],...], color?, strokeWeight?}]
 * @param {string} props.chartData.title - 지도 제목
 * @param {Object} props.chartData.options - 지도 옵션 {zoom, mapTypeId, etc}
 * @param {Object} props.error - 오류 상태
 * @param {boolean} props.debugMode - 디버그 모드
 */
const NaverMapRenderer = React.memo(({ chartData, error = null, debugMode = false }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const pathsRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  
  // 네이버 지도 SDK 로드
  useEffect(() => {
    const loadNaverMapsAPI = () => {
      return new Promise((resolve, reject) => {
        // 이미 로드되어 있는지 확인
        if (window.naver && window.naver.maps) {
          resolve(window.naver.maps);
          return;
        }
        
        // 네이버 지도 클라이언트 ID 확인
         const clientId = process.env.REACT_APP_NAVER_MAP_CLIENT_ID;
        if (!clientId) {
          reject(new Error('네이버 지도 클라이언트 ID가 설정되지 않았습니다.'));
          return;
        }
        
        // 스크립트 태그 생성
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`;
        script.async = true;
        
        script.onload = () => {
          if (window.naver && window.naver.maps) {
            resolve(window.naver.maps);
          } else {
            reject(new Error('네이버 지도 API 로드에 실패했습니다.'));
          }
        };
        
        script.onerror = (error) => {
          console.error('[NaverMap] 스크립트 로드 오류:', error);
          reject(new Error('네이버 지도 스크립트 로드에 실패했습니다. API 키를 확인해주세요.'));
        };
        
        // 중복 로드 방지
        const existingScript = document.querySelector(`script[src*="oapi.map.naver.com"]`);
        if (!existingScript) {
          document.head.appendChild(script);
        } else {
          // 기존 스크립트가 있으면 로드 완료를 기다림
          const checkLoaded = setInterval(() => {
            if (window.naver && window.naver.maps) {
              clearInterval(checkLoaded);
              resolve(window.naver.maps);
            }
          }, 100);
          
          // 타임아웃 설정
          setTimeout(() => {
            clearInterval(checkLoaded);
            reject(new Error('네이버 지도 API 로드 타임아웃 - API 키 또는 도메인 설정을 확인해주세요.'));
          }, 10000);
        }
      });
    };

    const initializeMap = async () => {
      try {
        setMapLoading(true);
        setMapError(null);
        
        if (debugMode) {
          console.log('[NaverMap] 지도 초기화 시작', chartData);
        }
        
        // 네이버 지도 API 로드
        const naverMaps = await loadNaverMapsAPI();
        
        if (!mapRef.current) {
          throw new Error('지도 컨테이너가 없습니다.');
        }
        
        // 기존 지도 인스턴스 정리 (안전하게)
        if (mapInstanceRef.current) {
          try {
            // 기존 마커들 정리
            markersRef.current.forEach(marker => {
              if (marker && typeof marker.setMap === 'function') {
                try {
                  marker.setMap(null);
                } catch (e) {
                  console.warn('[NaverMap] 마커 정리 중 오류 (무시됨):', e);
                }
              }
            });
            markersRef.current = [];
            
            // 기존 경로들 정리
            pathsRef.current.forEach(path => {
              if (path && typeof path.setMap === 'function') {
                try {
                  path.setMap(null);
                } catch (e) {
                  console.warn('[NaverMap] 경로 정리 중 오류 (무시됨):', e);
                }
              }
            });
            pathsRef.current = [];
          } catch (e) {
            console.warn('[NaverMap] 기존 인스턴스 정리 중 오류 (무시됨):', e);
          }
          
          mapInstanceRef.current = null;
        }
        
        // 기본 설정
        const defaultCenter = { lat: 37.5665, lng: 126.9780 }; // 서울 시청
        const center = chartData?.data?.center || defaultCenter;
        const zoom = chartData?.options?.zoom || 12;
        const mapTypeId = chartData?.options?.mapTypeId || 'normal';
        
        // 지도 옵션 설정
        const mapOptions = {
          center: new naverMaps.LatLng(center.lat, center.lng),
          zoom: zoom,
          mapTypeId: mapTypeId,
          zoomControl: true,
          zoomControlOptions: {
            position: naverMaps.Position.TOP_RIGHT
          },
          mapTypeControl: true,
          mapTypeControlOptions: {
            position: naverMaps.Position.TOP_LEFT
          },
          scaleControl: true,
          logoControl: true,
          mapDataControl: true
        };
        
        // 지도 생성
        const map = new naverMaps.Map(mapRef.current, mapOptions);
        mapInstanceRef.current = map;
        
        if (debugMode) {
          console.log('[NaverMap] 지도 생성 완료', map);
        }
        
        // 마커 생성
        if (chartData?.data?.markers && Array.isArray(chartData.data.markers)) {
          createMarkers(naverMaps, map, chartData.data.markers);
        }
        
        // 경로 생성
        if (chartData?.data?.paths && Array.isArray(chartData.data.paths)) {
          createPaths(naverMaps, map, chartData.data.paths);
        }
        
        // 정보창 초기화
        try {
          infoWindowRef.current = new naverMaps.InfoWindow({
            anchorSkew: true
          });
        } catch (e) {
          console.warn('[NaverMap] 정보창 초기화 실패 (무시됨):', e);
        }
        
        setMapLoading(false);
        
      } catch (error) {
        console.error('[NaverMap] 지도 초기화 오류:', error);
        setMapError(error.message);
        setMapLoading(false);
        
        // 초기화 실패 시에도 refs 정리
        markersRef.current = [];
        pathsRef.current = [];
        mapInstanceRef.current = null;
      }
    };

    if (chartData && chartData.viz_type === 'navermap') {
      initializeMap();
    }
    
    // 컴포넌트 언마운트 시 정리 (강화된 안전 장치)
    return () => {
      try {
        // 마커 정리 (안전하게)
        if (Array.isArray(markersRef.current)) {
          markersRef.current.forEach((marker, index) => {
            try {
              if (marker && typeof marker.setMap === 'function') {
                marker.setMap(null);
              }
            } catch (e) {
              console.warn(`[NaverMap] 마커 ${index} 정리 중 오류 (무시됨):`, e);
            }
          });
        }
        markersRef.current = [];
        
        // 경로 정리 (안전하게)
        if (Array.isArray(pathsRef.current)) {
          pathsRef.current.forEach((path, index) => {
            try {
              if (path && typeof path.setMap === 'function') {
                path.setMap(null);
              }
            } catch (e) {
              console.warn(`[NaverMap] 경로 ${index} 정리 중 오류 (무시됨):`, e);
            }
          });
        }
        pathsRef.current = [];
        
        // 정보창 정리 (안전하게)
        try {
          if (infoWindowRef.current && typeof infoWindowRef.current.close === 'function') {
            infoWindowRef.current.close();
          }
        } catch (e) {
          console.warn('[NaverMap] 정보창 정리 중 오류 (무시됨):', e);
        }
        infoWindowRef.current = null;
        
        // 지도 인스턴스 정리
        mapInstanceRef.current = null;
        
      } catch (e) {
        console.warn('[NaverMap] cleanup 중 전체 오류 (무시됨):', e);
      }
    };
  }, [chartData, debugMode]);

  // 마커 생성 함수
  const createMarkers = (naverMaps, map, markers) => {
    if (debugMode) {
      console.log('[NaverMap] 마커 생성 시작', markers);
    }
    
    // 입력 검증
    if (!naverMaps || !map || !Array.isArray(markers)) {
      console.warn('[NaverMap] 마커 생성 실패: 잘못된 입력 파라미터');
      return;
    }
    
    // 마커가 없는 경우 처리
    if (markers.length === 0) {
      console.warn('[NaverMap] 마커가 없습니다');
      return;
    }
    
    markers.forEach((markerData, index) => {
      try {
        // 마커 데이터 검증
        if (!markerData || typeof markerData.lat !== 'number' || typeof markerData.lng !== 'number') {
          console.warn(`[NaverMap] 마커 ${index}: 잘못된 좌표 데이터`, markerData);
          return;
        }
        
        // 좌표 유효성 검사
        if (isNaN(markerData.lat) || isNaN(markerData.lng)) {
          console.warn(`[NaverMap] 마커 ${index}: 유효하지 않은 좌표`, markerData);
          return;
        }
        
        const marker = new naverMaps.Marker({
          position: new naverMaps.LatLng(markerData.lat, markerData.lng),
          map: map,
          title: markerData.title || `마커 ${index + 1}`,
          icon: markerData.icon || {
            content: '<div style="background-color: #ff4444; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
            anchor: new naverMaps.Point(10, 10)
          }
        });
        
        // 클릭 이벤트 추가 (정보창 표시)
        if (markerData.content) {
          try {
            naverMaps.Event.addListener(marker, 'click', () => {
              try {
                if (infoWindowRef.current && typeof infoWindowRef.current.setContent === 'function') {
                  infoWindowRef.current.setContent(`
                    <div style="padding: 10px; min-width: 200px;">
                      <h4 style="margin: 0 0 8px 0; color: #333;">${markerData.title || '정보'}</h4>
                      <p style="margin: 0; color: #666; font-size: 14px;">${markerData.content}</p>
                    </div>
                  `);
                  infoWindowRef.current.open(map, marker);
                }
              } catch (e) {
                console.warn(`[NaverMap] 마커 ${index} 클릭 이벤트 처리 중 오류:`, e);
              }
            });
          } catch (e) {
            console.warn(`[NaverMap] 마커 ${index} 이벤트 리스너 추가 실패:`, e);
          }
        }
        
        markersRef.current.push(marker);
        
      } catch (error) {
        console.error(`[NaverMap] 마커 ${index} 생성 오류:`, error);
      }
    });
    
    if (debugMode) {
      console.log(`[NaverMap] 마커 ${markersRef.current.length}개 생성 완료`);
    }
  };

  // 경로 생성 함수
  const createPaths = (naverMaps, map, paths) => {
    if (debugMode) {
      console.log('[NaverMap] 경로 생성 시작', paths);
    }
    
    // 입력 검증
    if (!naverMaps || !map || !Array.isArray(paths)) {
      console.warn('[NaverMap] 경로 생성 실패: 잘못된 입력 파라미터');
      return;
    }
    
    paths.forEach((pathData, index) => {
      try {
        if (!pathData || !pathData.coords || !Array.isArray(pathData.coords)) {
          console.warn(`[NaverMap] 경로 ${index} 좌표 데이터가 올바르지 않습니다.`, pathData);
          return;
        }
        
        // 좌표 배열을 LatLng 객체 배열로 변환
        const pathCoords = pathData.coords.map((coord, coordIndex) => {
          try {
            if (Array.isArray(coord) && coord.length >= 2) {
              const lat = parseFloat(coord[0]);
              const lng = parseFloat(coord[1]);
              if (isNaN(lat) || isNaN(lng)) {
                console.warn(`[NaverMap] 경로 ${index}, 좌표 ${coordIndex}: 유효하지 않은 숫자`, coord);
                return null;
              }
              return new naverMaps.LatLng(lat, lng);
            } else if (coord && typeof coord.lat === 'number' && typeof coord.lng === 'number') {
              if (isNaN(coord.lat) || isNaN(coord.lng)) {
                console.warn(`[NaverMap] 경로 ${index}, 좌표 ${coordIndex}: 유효하지 않은 숫자`, coord);
                return null;
              }
              return new naverMaps.LatLng(coord.lat, coord.lng);
            } else {
              console.warn(`[NaverMap] 경로 ${index}, 좌표 ${coordIndex}: 올바르지 않은 좌표 형식`, coord);
              return null;
            }
          } catch (e) {
            console.warn(`[NaverMap] 경로 ${index}, 좌표 ${coordIndex} 변환 오류:`, e);
            return null;
          }
        }).filter(coord => coord !== null);
        
        if (pathCoords.length < 2) {
          console.warn(`[NaverMap] 경로 ${index}는 최소 2개 이상의 유효한 좌표가 필요합니다. 현재: ${pathCoords.length}개`);
          return;
        }
        
        const polyline = new naverMaps.Polyline({
          path: pathCoords,
          strokeColor: pathData.color || '#ff0000',
          strokeWeight: pathData.strokeWeight || 3,
          strokeOpacity: pathData.strokeOpacity || 0.8,
          map: map
        });
        
        pathsRef.current.push(polyline);
        
      } catch (error) {
        console.error(`[NaverMap] 경로 ${index} 생성 오류:`, error);
      }
    });
    
    if (debugMode) {
      console.log(`[NaverMap] 경로 ${pathsRef.current.length}개 생성 완료`);
    }
  };

  // 데이터 상태 확인 함수
  const hasValidLocationData = () => {
    if (!chartData?.data) return false;
    
    const markers = chartData.data.markers || [];
    const paths = chartData.data.paths || [];
    
    // 유효한 마커가 있는지 확인
    const hasValidMarkers = markers.some(marker => 
      marker && 
      typeof marker.lat === 'number' && 
      typeof marker.lng === 'number' &&
      !isNaN(marker.lat) && 
      !isNaN(marker.lng)
    );
    
    // 유효한 경로가 있는지 확인
    const hasValidPaths = paths.some(path =>
      path &&
      Array.isArray(path.coords) &&
      path.coords.length >= 2 &&
      path.coords.every(coord => 
        Array.isArray(coord) && 
        coord.length >= 2 && 
        typeof coord[0] === 'number' && 
        typeof coord[1] === 'number' &&
        !isNaN(coord[0]) && 
        !isNaN(coord[1])
      )
    );
    
    return hasValidMarkers || hasValidPaths;
  };

  // 백엔드 메시지 가져오기
  const getBackendMessage = () => {
    if (!chartData?.data) return null;
    
    // 메인 메시지 (데이터 없음)
    if (chartData.data.message) {
      return chartData.data.message;
    }
    
    // 필터링 메시지 (일부 데이터 제외됨)
    if (chartData.data.filter_message) {
      return chartData.data.filter_message;
    }
    
    return null;
  };

  // 렌더링
  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        color: '#d32f2f',
        padding: '20px',
        textAlign: 'center',
        border: '1px solid #ffcdd2',
        borderRadius: '4px'
      }}>
        <div>
          <strong>지도 오류:</strong><br />
          {error}
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        color: '#d32f2f',
        padding: '20px',
        textAlign: 'center',
        border: '1px solid #ffcdd2',
        borderRadius: '4px'
      }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <strong>네이버 지도 로드 오류</strong><br />
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
            {mapError}
          </div>
          {mapError.includes('API 키') || mapError.includes('인증') || mapError.includes('타임아웃') ? (
            <div style={{ 
              marginTop: '12px', 
              padding: '8px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#856404'
            }}>
              💡 해결 방법:<br />
              • 네이버 클라우드 플랫폼에서 API 키 확인<br />
              • 도메인 등록 상태 확인<br />
              • HTTPS 환경에서 접속 시도
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', minHeight: '300px', position: 'relative' }}>
      {/* 제목 표시 */}
      {chartData?.title && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: '20px',
          fontWeight: '600',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          color: '#2c3e50',
          lineHeight: 1.2,
          maxWidth: '300px'
        }}>
          {chartData.title}
        </div>
      )}
      
      {/* 위치 정보 없음 메시지 */}
      {!mapLoading && !hasValidLocationData() && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '24px 32px',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          textAlign: 'center',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#555',
            marginBottom: '8px'
          }}>
            위치 정보 없음
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#888',
            lineHeight: 1.4
          }}>
            {getBackendMessage() || '현재 표시할 위치 데이터가 없습니다.\n데이터 수집 후 다시 확인해주세요.'}
          </div>
        </div>
      )}
      
      {/* 필터링 메시지 (데이터는 있지만 일부 제외된 경우) */}
      {!mapLoading && hasValidLocationData() && chartData?.data?.filter_message && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 193, 7, 0.9)',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#856404',
          maxWidth: '300px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #ffeaa7'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>⚠️ 데이터 필터링 알림</div>
          <div>{chartData.data.filter_message}</div>
        </div>
      )}
      
      {/* 로딩 인디케이터 */}
      {mapLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div>지도 로드 중...</div>
        </div>
      )}
      
      {/* 지도 컨테이너 */}
      <div 
        ref={mapRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          minHeight: '300px',
          backgroundColor: '#f5f5f5' 
        }} 
      />
      
      {/* 디버그 정보 */}
      {debugMode && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
          maxWidth: '200px'
        }}>
          <div>마커: {markersRef.current.length}개</div>
          <div>경로: {pathsRef.current.length}개</div>
          <div>중심: {chartData?.data?.center?.lat?.toFixed(4)}, {chartData?.data?.center?.lng?.toFixed(4)}</div>
          <div>위치 데이터: {hasValidLocationData() ? '있음' : '없음'}</div>
        </div>
      )}
    </div>
  );
});

export default NaverMapRenderer; 